import React, { useState, useContext, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { collection, query, where, onSnapshot, doc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { AuthContext } from '../../context/AuthContext';
import PageLayout from '../../components/layout/PageLayout';
import ConversationListItem from '../../components/chat/ConversationListItem';
import { ChatIcon, PlusIcon } from '../../components/Icons/svgIcons';
import EmptyState from '../../components/data-display/EmptyState';
import ChatEmptyState from '../../components/chat/ChatEmptyState';
import SelectTeamMemberModal from '../../components/modals/SelectTeamMemberModal';
import { apiClient } from '../../services/apiClient';
import { API_ENDPOINTS } from '../../config/api';

const ConversationsPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [apiConversations, setApiConversations] = useState([]);
  const [firestoreRooms, setFirestoreRooms] = useState({});
  const [hasReceivedSnapshot, setHasReceivedSnapshot] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { user, firebaseUser } = useContext(AuthContext);
  const currentUserUid = user?.id || firebaseUser?.uid;
  const isOnline = useSelector(state => state.ui.networkStatus.isOnline);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // ── Helper to build conversation list from API response ─────────────────────
  const buildConversationsList = useCallback((roomsList) => {
    if (!roomsList || roomsList.length === 0) return [];
    return roomsList
      .filter(room => room.room_id)
      .map(room => {
        const rid = room.room_id;
        let otherUid = '';
        if (rid) {
          const parts = String(rid).split('_');
          otherUid = parts.find(p => String(p) !== String(currentUserUid));
        }

        const isCurrentUserSender = String(currentUserUid) === String(room.sender_id);
        const partnerInfo = isCurrentUserSender ? room.receiver : room.sender;

        otherUid = otherUid || (isCurrentUserSender ? room.receiver_id : room.sender_id) || partnerInfo?.id;
        
        const partnerName =
          partnerInfo?.full_name ||
          `${partnerInfo?.first_name || ''} ${partnerInfo?.last_name || ''}`.trim() ||
          'Project Manager';
        const partnerAvatar = partnerInfo?.profile_image || null;

        let initLastTimeRaw = 0;
        let initTimestamp = null;
        if (room.updated_at) {
          try {
            const d = new Date(room.updated_at);
            initLastTimeRaw = d.getTime();
            initTimestamp = d.toISOString();
          } catch (e) {}
        }

        return {
          id: otherUid || rid,
          rid,
          name: partnerName,
          avatar: partnerAvatar,
          lastMessage: '',
          isLoadingMessage: true,
          timestamp: initTimestamp,
          unreadCount: 0,
          lastTimeRaw: initLastTimeRaw,
        };
      });
  }, [currentUserUid]);

  // ── Load rooms from API and render immediately ─────────────────────────────
  const fetchRoomsAndEnrich = useCallback(async () => {
    if (!currentUserUid || !isOnline) {
      setLoading(false);
      return;
    }

    const cacheKey = `chat_rooms_cache_${currentUserUid}`;

    try {
      // 1. Instant Load from Cache
      const cachedData = localStorage.getItem(cacheKey);
      if (cachedData) {
        try {
          const cachedRooms = JSON.parse(cachedData);
          if (cachedRooms.length > 0) {
            const cachedConversations = buildConversationsList(cachedRooms);
            setApiConversations(cachedConversations);
            setLoading(false);
          }
        } catch (e) {
          console.warn('[ConversationsPage] Error parsing cached rooms', e);
        }
      }

      console.log('[ConversationsPage] Fetching active rooms from Laravel API...');
      const response = await apiClient.get(API_ENDPOINTS.chats.rooms);

      // Handle Laravel pagination wrapping
      const roomsList = response.data?.data?.data || response.data?.data || response.data || [];
      
      // Update local cache
      localStorage.setItem(cacheKey, JSON.stringify(roomsList));

      if (!isMountedRef.current) return;

      if (roomsList.length === 0) {
        setApiConversations([]);
        setLoading(false);
        return;
      }

      console.log(`[ConversationsPage] Found ${roomsList.length} rooms.`);

      // 2. Update state with fresh data
      const allConversations = buildConversationsList(roomsList);
      setApiConversations(allConversations);
      setLoading(false);
    } catch (err) {
      console.error('🔴 Failed to fetch active rooms from API:', err);
      if (isMountedRef.current) setLoading(false);
    }
  }, [currentUserUid, isOnline, buildConversationsList]);

  useEffect(() => {
    fetchRoomsAndEnrich();
  }, [fetchRoomsAndEnrich]);

  // ── Polling when no rooms exist ───────────────────────────────────────────
  useEffect(() => {
    if (!currentUserUid || loading || !isOnline) return;

    if (apiConversations.length === 0) {
      const timer = setInterval(() => {
        fetchRoomsAndEnrich();
      }, 5000);
      return () => clearInterval(timer);
    }
  }, [currentUserUid, loading, isOnline, apiConversations.length, fetchRoomsAndEnrich]);

  // ── Real-time Firestore sync for all rooms ──────────────────────────────────
  useEffect(() => {
    if (apiConversations.length === 0 || !isOnline) {
      setHasReceivedSnapshot(true);
      return;
    }

    console.log('[ConversationsPage] Attaching real-time room listeners for rooms:', apiConversations.map(c => c.rid));

    const unsubscribes = apiConversations.map(conv => {
      const roomRef = doc(db, 'chats', conv.rid);
      return onSnapshot(roomRef, (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          console.log(`[ConversationsPage] Snapshot received for room ${conv.rid}:`, {
            last_message: data.last_message,
            unreadCounts: data.unreadCounts,
            currentUserUid: String(currentUserUid),
            currentUserUnread: data.unreadCounts?.[String(currentUserUid)]
          });
          setFirestoreRooms(prev => ({
            ...prev,
            [conv.rid]: data
          }));
        }
      }, (err) => {
        console.warn(`[ConversationsPage] Failed to listen to room ${conv.rid}:`, err.message);
      });
    });

    setHasReceivedSnapshot(true);

    return () => {
      console.log('[ConversationsPage] Detaching all room snapshot listeners');
      unsubscribes.forEach(unsub => unsub());
    };
  }, [apiConversations, isOnline]);

  // ── Reactive computed list merging API & Firestore states ──────────────────
  const conversations = useMemo(() => {
    if (apiConversations.length === 0) return [];

    const merged = apiConversations.map(conv => {
      const roomData = firestoreRooms[conv.rid];
      if (roomData) {
        const lastMsg = roomData.last_message || 'No messages yet';
        const unread = roomData.unreadCounts?.[String(currentUserUid)] || 0;
        let timestamp = conv.timestamp;
        let lastTimeRaw = conv.lastTimeRaw || 0;
        
        if (roomData.last_time) {
          try {
            const date = typeof roomData.last_time.toDate === 'function' 
              ? roomData.last_time.toDate() 
              : new Date(roomData.last_time);
            timestamp = date.toISOString();
            lastTimeRaw = date.getTime();
          } catch (e) {}
        } else if (roomData.last_message && roomData.last_message !== 'No messages yet') {
          // Fallback to local system time if serverTimestamp hasn't resolved yet
          lastTimeRaw = Date.now();
          timestamp = new Date().toISOString();
        }
        
        return {
          ...conv,
          lastMessage: lastMsg,
          isLoadingMessage: false,
          timestamp,
          unreadCount: unread,
          lastTimeRaw
        };
      }
      
      // If no Firestore room doc found, show 'No messages yet' if the snapshot loaded.
      return {
        ...conv,
        lastMessage: 'No messages yet',
        isLoadingMessage: !hasReceivedSnapshot,
      };
    });

    // Sort by latest message timestamp
    return merged.sort((a, b) => b.lastTimeRaw - a.lastTimeRaw);
  }, [apiConversations, firestoreRooms, hasReceivedSnapshot, currentUserUid]);

  const filteredConversations = conversations.filter(
    conv =>
      conv.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      conv.lastMessage.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // ── Offline state ────────────────────────────────────────────────────────────
  if (!isOnline) {
    return (
      <PageLayout title="Messages" showStatus>
        <div className="flex flex-col items-center justify-center h-full bg-gray-50 -mx-4 -mt-4 p-6 text-center">
          <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center mb-5">
            <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636a9 9 0 010 12.728m0 0l-2.829-2.829m2.829 2.829L21 21M15.536 8.464a5 5 0 010 7.072m0 0l-2.829-2.829m-4.243 2.829a4.978 4.978 0 01-1.414-2.83m-1.414 5.658a9 9 0 01-2.167-9.238m7.824 2.167a1 1 0 111.414 1.414m-1.414-1.414L3 3" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-800 mb-3">Chat is Offline</h2>
          <p className="text-gray-500 text-sm max-w-[260px] leading-relaxed">
            You need an active internet connection to send and receive messages.
          </p>
        </div>
      </PageLayout>
    );
  }

  return (
    <div className="h-full relative overflow-hidden">
      <PageLayout title="Messages" showStatus onRefresh={fetchRoomsAndEnrich}>
        <div className="flex flex-col h-full bg-white -mx-4 -mt-4">
          {/* Search bar — only visible when there are conversations */}
          {!loading && apiConversations.length > 0 && (
            <div className="p-4 border-b border-gray-100">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search conversations..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="w-full bg-gray-100 border-0 rounded-xl px-10 py-2.5 text-sm focus:ring-2 focus:ring-[var(--btn-primary-bg)]/20 transition-all"
                />
                <div className="absolute left-3 top-1/2 -translate-y-1/2">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </div>
            </div>
          )}

          <div className="flex-1 overflow-y-auto px-2 py-2">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <div className="w-8 h-8 border-2 border-[var(--btn-primary-bg)] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : filteredConversations.length === 0 ? (
              searchQuery ? (
                <EmptyState
                  icon={<ChatIcon className="w-14 h-14" />}
                  title="No conversations found"
                  message="Try searching for someone else."
                />
              ) : (
                <ChatEmptyState onStartChat={() => setIsModalOpen(true)} />
              )
            ) : (
              <div className="space-y-1">
                {filteredConversations.map(conv => (
                  <ConversationListItem key={conv.id} conversation={conv} />
                ))}
              </div>
            )}
          </div>
        </div>
      </PageLayout>

      {/* + FAB — only visible when there are conversations */}
      {!loading && apiConversations.length > 0 && (
        <button
          onClick={() => setIsModalOpen(true)}
          className="fixed bottom-20 right-4 md:absolute md:bottom-8 md:right-6 w-14 h-14 bg-[var(--btn-secondary-bg)] rounded-full flex items-center justify-center shadow-lg shadow-blue-900/20 transform transition-all hover:scale-105 active:scale-95 z-40 hover:shadow-xl border-2 border-white"
          aria-label="New message"
        >
          <PlusIcon className="w-7 h-7 text-white" />
        </button>
      )}

      <SelectTeamMemberModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
};

export default ConversationsPage;
